import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import ManageOEMModal from './ManageOEMModal';
import ManageSKUModal from './ManageSKUModal';
import KittingModal from './KittingModule/KittingModal';
import VehicleLookupModal from './VehicleLookupModal';
import { fetchOrderwiseToken, postOrderwise } from '../orderwiseApi';

const KIT_ID_PREFIX = 'OEMKIT';
const STOCK_CACHE_PRIME_INTERVAL_MS = 10 * 60 * 1000;
const defaultFilters = {
  Make: '',
  ModelName: '',
  Year: '',
  Region: '',
  YearID: '',
  ModelID: '',
  ItemType: '',
  FitmentLocation: '',
  OEMNumber: '',
  SKU: '',
  SKUGroup: '',
  Quantity: '',
  OEMNote: '',
  AdditionalFitmentInfo: ''
};
const editableFields = ['SKUGroup', 'OEMNote', 'AdditionalFitmentInfo', 'OEMNumber', 'Quantity'];
const ACTION_COL = 72;
const ADD_COLUMN_WIDTH = 260;
const colStyle = (w) => ({ width: `${w}px`, minWidth: `${w}px`, flex: `0 0 ${w}px` });

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

function PartImages({ params, pricingReady = true }) {
  const [availableImages, setAvailableImages] = useState([]);
  const imageCache = useRef(new Map());

  const doesImageExist = useCallback(async (url) => {
    if (!url) return false;
    if (imageCache.current.has(url)) return imageCache.current.get(url);
    const result = await new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
    imageCache.current.set(url, result);
    return result;
  }, []);

  if (!params || !params.data) return null;
  const skus = params.data.SKU ? params.data.SKU.split(',').map(s => s.trim()) : [];
  const filteredSkus = skus.filter(sku => sku.toUpperCase() !== '999999H');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!filteredSkus.length || !pricingReady) {
        setAvailableImages([]);
        return;
      }
      const candidates = filteredSkus.flatMap(sku =>
        [1, 2, 3].map(n => ({ sku, url: `https://hendler.co.uk/images/products/${sku}_${n}.jpg` }))
      );
      const checks = await Promise.all(candidates.map(async entry => {
        const exists = await doesImageExist(entry.url);
        return exists ? entry : null;
      }));
      if (!cancelled) {
        setAvailableImages(checks.filter(Boolean));
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [params?.data?.SKU, pricingReady]);

  const openGallery = () => {
    if (!availableImages.length) return;
    const theme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    const win = window.open('', '_blank', 'width=900,height=700');
    const first = availableImages[0];
    const payload = availableImages.map(({ sku, url }) => ({ sku, url }));

    win.document.write(`
      <!DOCTYPE html>
      <html class="${theme}">
        <head>
          <meta charset="utf-8" />
          <title>Gallery – ${first?.sku || ''}</title>
          <style>
            :root.dark { --modal-bg: #2c2c2c; --modal-text: #f0cce8; }
            :root.light { --modal-bg: #ffffff; --modal-text: #1a3c5e; }
            body { margin: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; background-color: var(--modal-bg); color: var(--modal-text); font-family: sans-serif; }
            .viewer { max-width: 80vw; max-height: 80vh; margin: 1rem 0; }
            button { font-size: 1.2rem; margin: 0 1rem; cursor: pointer; background: none; border: 1px solid var(--modal-text); color: var(--modal-text); padding: 0.5rem 1rem; border-radius: 4px; }
            button:hover { opacity: 0.8; }
          </style>
        </head>
        <body>
          <h2 id="skuLabel">${first?.sku || ''} (${availableImages.length} image${availableImages.length>1?'s':''})</h2>
          <div style="display:flex;align-items:center;justify-content:center;">
            <button id="prev">⟨ Prev</button>
            <img id="viewer" src="${first?.url || ''}" class="viewer" />
            <button id="next">Next ⟩</button>
          </div>
          <script>
            const urls = ${JSON.stringify(payload)};
            let idx = 0;
            const img = document.getElementById('viewer');
            const lbl = document.getElementById('skuLabel');
            document.getElementById('prev').onclick = () => {
              idx = (idx - 1 + urls.length) % urls.length;
              img.src = urls[idx].url;
              lbl.textContent = urls[idx].sku + ' (' + (idx+1) + '/' + urls.length + ')';
            };
            document.getElementById('next').onclick = () => {
              idx = (idx + 1) % urls.length;
              img.src = urls[idx].url;
              lbl.textContent = urls[idx].sku + ' (' + (idx+1) + '/' + urls.length + ')';
            };
            document.body.onclick = e => { if (e.target === document.body) window.close(); };
          </script>
        </body>
      </html>
    `);
    win.document.close();
  };

  const count = availableImages.length;
  const disabled = count === 0;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={openGallery}
      title={disabled ? 'No images available' : `Open ${count} image${count === 1 ? '' : 's'}`}
    >
      Images{count > 0 ? ` (${count})` : ''}
    </button>
  );
}

export default function PartFinderPage({
  permissions = {},
  isDark = false,
  allowTradePrices = false,
  onAddToOrder,
  orderAccountNumber = '',
  hideManagementColumns = false,
  fullHeight = false,
  mode = 'write',
  highlightOutOfStockRows = false
}) {
  const isReadOnly = mode === 'readonly';
  const [showStock, setShowStock] = useState(() => {
    try {
      const stored = localStorage.getItem('pfShowStock');
      return stored != null ? JSON.parse(stored) : true;
    } catch {
      return true;
    }
  });
  const [showPrices, setShowPrices] = useState(() => {
    if (!allowTradePrices) return false;
    try {
      const stored = localStorage.getItem('pfShowPrices');
      if (stored != null) return JSON.parse(stored);
    } catch {}
    // Default to true when embedded in order entry (has add-to-order handler)
    if (onAddToOrder) return true;
    return false;
  });
  const [fitments, setFitments] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);
  const [globalSearch, setGlobalSearch] = useState('');
  const [rowLimit, setRowLimit] = useState(100);
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [pricingHydrated, setPricingHydrated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [editing, setEditing] = useState({ fitmentKey: null, field: '', value: '' });
  const debouncedFilters = useDebounce(filters, 300);
  const debouncedGlobalSearch = useDebounce(globalSearch, 300);
  const lastRowRef = useRef(null);
  const listRef = useRef(null);
  const [currentViewPage, setCurrentViewPage] = useState(1);
  const [recentSearches, setRecentSearches] = useState(() => {
    try { return JSON.parse(localStorage.getItem('recentSearches')) || []; }
    catch { return []; }
  });
  const [recentSelectValue, setRecentSelectValue] = useState('');
  const abortControllerRef = useRef(null);
  const tableRef = useRef(null);
  const [oemModalVisible, setOemModalVisible] = useState(false);
  const [skuModalVisible, setSkuModalVisible] = useState(false);
  const [selectedOEMNumber, setSelectedOEMNumber] = useState('');
  const [selectedOEMKey, setSelectedOEMKey] = useState(null);
  const [selectedSKUGroup, setSelectedSKUGroup] = useState('');
  const [kitModalState, setKitModalState] = useState({ open: false, kitId: null, fitment: null });
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [historyFitmentKey, setHistoryFitmentKey] = useState(null);
  const [showVehicleLookup, setShowVehicleLookup] = useState(false);
  const [showHistoryHighlights, setShowHistoryHighlights] = useState(() => {
    if (mode === 'readonly') return false;
    try {
      return JSON.parse(localStorage.getItem('showHistoryHighlights') ?? 'true');
    } catch {
      return true;
    }
  });
  const [skuDetailsState, setSkuDetailsState] = useState({});
  const skuDetailsRef = useRef({});
  const skuPendingRef = useRef(new Set());
  const [kitAvailabilityState, setKitAvailabilityState] = useState({});
  const kitAvailabilityRef = useRef({});
  const kitPendingRef = useRef(new Set());
  const orderwiseTokenRef = useRef(null);
  const orderwiseStockCachePrimeRef = useRef({ promise: null, lastPrime: 0 });
  const isOrderEntryEmbed = Boolean(onAddToOrder);
  const isFetching = useRef(false);
  const filtersScrollRef = useRef(null);
  const modelNameInputRef = useRef(null);
  const [activeRowIndex, setActiveRowIndex] = useState(-1);
  const [addFeedback, setAddFeedback] = useState('');
  const [imageModal, setImageModal] = useState({ open: false, items: [], index: 0, label: '' });
  const imageProbeCacheRef = useRef(new Map());
  const addFeedbackTimeoutRef = useRef(null);
  const gridRef = useRef(null);
  const rowRefs = useRef(new Map());
  const [addDrafts, setAddDrafts] = useState({});
  const [pendingSkuFocus, setPendingSkuFocus] = useState(false);
  const gridHeight = useMemo(() => (fullHeight ? '100%' : '80vh'), [fullHeight]);

  useEffect(() => {
    if (!imageModal.open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setImageModal({ open: false, items: [], index: 0, label: '' });
        return;
      }
      if (!imageModal.items.length) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setImageModal((prev) => {
          const total = prev.items.length;
          const next = (prev.index - 1 + total) % total;
          return { ...prev, index: next, label: prev.items[next]?.sku || prev.label };
        });
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setImageModal((prev) => {
          const total = prev.items.length;
          const next = (prev.index + 1) % total;
          return { ...prev, index: next, label: prev.items[next]?.sku || prev.label };
        });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [imageModal]);

  useEffect(() => {
    skuDetailsRef.current = skuDetailsState;
  }, [skuDetailsState]);


  useEffect(() => {
    if (!isOrderEntryEmbed) return undefined;
    const handler = (e) => {
      if (e.key !== 'F2') return;
      e.preventDefault();
      if (!modelNameInputRef.current) return;
      modelNameInputRef.current.focus();
      modelNameInputRef.current.select?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOrderEntryEmbed]);

  // No auto-focus; F2 handler below is the only focus path.

  useEffect(() => {
    const gridEl = gridRef.current;
    if (!gridEl) return;
    const row = fitments[activeRowIndex];
    if (!row) return;
    const key = row.FitmentKey ?? row.fitmentKey ?? activeRowIndex;
    const el = rowRefs.current.get(key);
    if (!el) return;
    const gridRect = gridEl.getBoundingClientRect();
    const rowRect = el.getBoundingClientRect();
    const margin = 12;
    if (rowRect.top < gridRect.top + margin) {
      gridEl.scrollTop -= (gridRect.top + margin) - rowRect.top;
    } else if (rowRect.bottom > gridRect.bottom - margin) {
      gridEl.scrollTop += rowRect.bottom - (gridRect.bottom - margin);
    }
  }, [activeRowIndex, fitments]);

  useEffect(() => () => {
    if (addFeedbackTimeoutRef.current) clearTimeout(addFeedbackTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (activeRowIndex >= fitments.length) {
      setActiveRowIndex(fitments.length ? fitments.length - 1 : -1);
    }
  }, [fitments.length, activeRowIndex]);

  useEffect(() => {
    kitAvailabilityRef.current = kitAvailabilityState;
  }, [kitAvailabilityState]);

  useEffect(() => {
    try {
      localStorage.setItem('showHistoryHighlights', JSON.stringify(showHistoryHighlights));
    } catch {
      // ignore storage errors
    }
  }, [showHistoryHighlights]);

  useEffect(() => {
    localStorage.setItem('pfShowStock', JSON.stringify(showStock));
  }, [showStock]);

  useEffect(() => {
    if (allowTradePrices) {
      localStorage.setItem('pfShowPrices', JSON.stringify(showPrices));
    }
  }, [showPrices, allowTradePrices]);

  useEffect(() => {
    if (!allowTradePrices) {
      setShowPrices(false);
      return;
    }
    try {
      const stored = localStorage.getItem('pfShowPrices');
      if (stored != null) {
        setShowPrices(JSON.parse(stored));
      }
    } catch {
      // ignore
    }
  }, [allowTradePrices]);

  const extractKitId = useCallback((entity) => {
    if (!entity) return null;
    const direct = entity?.KitId ?? entity?.KitID ?? entity?.LinkedKitId;
    if (direct) return direct;
    const oemNumber = entity?.OEMNumber;
    if (typeof oemNumber === 'string' && oemNumber.toUpperCase().startsWith(KIT_ID_PREFIX)) {
      return oemNumber;
    }
    return null;
  }, []);

  const canEditDataCollection = permissions?.['Data Collection/Reviewing'] === 'write';
  const canEditPartFinder = permissions?.PartFinder === 'write';
  const kitPermission = permissions?.Kitting ?? 'none';
  const canEditFitments = !isReadOnly && (canEditDataCollection || canEditPartFinder);
  const canOpenKitModal = kitPermission !== 'none' || permissions?.PartFinder === 'write';
  const canEditKits = kitPermission === 'write' || permissions?.PartFinder === 'write';

  const primeOrderwiseStockCache = useCallback(async (force = false) => {
    const state = orderwiseStockCachePrimeRef.current;
    if (state.promise && !force) {
      return state.promise;
    }
    const now = Date.now();
    if (!force && state.lastPrime && (now - state.lastPrime) < STOCK_CACHE_PRIME_INTERVAL_MS) {
      return state.promise || null;
    }

    const task = (async () => {
      try {
        const tokenValue = await obtainOrderwiseToken(force);
        await postOrderwise(
          '/fetch-sku-stock?refresh=true',
          [],
          tokenValue,
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 300000
          }
        );
        state.lastPrime = Date.now();
      } catch (err) {
        throw err;
      } finally {
        state.promise = null;
      }
    })();

    state.promise = task;
    return task;
  }, []);

  const repullOrderwiseStockCacheIfExpired = useCallback(() => {
    const state = orderwiseStockCachePrimeRef.current;
    if (state.promise) return;
    const now = Date.now();
    const expired = !state.lastPrime || (now - state.lastPrime) >= STOCK_CACHE_PRIME_INTERVAL_MS;
    if (!expired) return;
    primeOrderwiseStockCache(false).catch(err => {
      console.warn('OrderWise stock cache refresh after expiry failed', err);
    });
  }, [primeOrderwiseStockCache]);

  const obtainOrderwiseToken = useCallback(async (force = false) => {
    if (!force && orderwiseTokenRef.current) {
      return orderwiseTokenRef.current;
    }
    const tokenValue = await fetchOrderwiseToken();
    if (!tokenValue) {
      throw new Error('OrderWise token unavailable');
    }
    orderwiseTokenRef.current = tokenValue;
    return tokenValue;
  }, []);

  const parseNumeric = useCallback((value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[^0-9.-]/g, '').trim();
      if (!cleaned) return null;
      const numeric = Number(cleaned);
      return Number.isFinite(numeric) ? numeric : null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }, []);

  const fetchSkuDetails = useCallback(async (skus = []) => {
    const normalized = Array.from(new Set(
      (Array.isArray(skus) ? skus : [])
        .map(item => (typeof item === 'string' ? item.trim().toUpperCase() : ''))
        .filter(Boolean)
    ));
    if (!normalized.length) return {};

    const current = skuDetailsRef.current;
    const toFetch = normalized.filter((sku) => {
      const existing = current[sku];
      if (!existing) return true;
      if (existing.loading) return false;
      if (existing.error) return true;
      return false;
    });

    if (toFetch.length) {
      setSkuDetailsState((prev) => {
        const next = { ...prev };
        let updated = false;
        toFetch.forEach((sku) => {
          if (skuPendingRef.current.has(sku)) return;
          skuPendingRef.current.add(sku);
          const existing = next[sku] || {};
          next[sku] = { ...existing, loading: true };
          updated = true;
        });
        if (!updated) return prev;
        skuDetailsRef.current = next;
        return next;
      });

      try {
        const tokenValue = await obtainOrderwiseToken(false);
        const headers = {
          'Content-Type': 'application/json'
        };
        if (orderAccountNumber) headers['X-Account-Number'] = orderAccountNumber;
        const body = orderAccountNumber
          ? { accountNumber: orderAccountNumber, variants: toFetch }
          : { variants: toFetch };
        const response = await postOrderwise(
          '/products/pricing',
          body,
          tokenValue,
          {
            headers,
            timeout: 300000,
          }
        );
        const items = Array.isArray(response?.items)
          ? response.items
          : Array.isArray(response)
          ? response
          : [];
        const bySku = new Map();
        items.forEach((item) => {
          const code = (item?.vad_variant_code || item?.VariantCode || item?.variant || item?.sku || '').toString().trim().toUpperCase();
          if (code) bySku.set(code, item);
        });

        toFetch.forEach((sku) => {
          const payload = bySku.get(sku);
          if (!payload) {
            setSkuDetailsState((prev) => {
              const next = { ...prev, [sku]: { stock: null, price: null, loading: false, error: 'No pricing row returned' } };
              skuDetailsRef.current = next;
              return next;
            });
            skuPendingRef.current.delete(sku);
            return;
          }

          const stockRaw = parseNumeric(
            payload.vasq_free_stock_quantity ??
              payload.FreeStock ??
              payload.freeStock ??
              payload.bn_free_stock ??
              payload.bn_free_stock_quantity ??
              payload.availableStock ??
              payload.available_stock
          );
          const stock = sku === '999999H' ? 0 : (Number.isFinite(stockRaw) ? stockRaw : null);

          const accountPriceRaw = parseNumeric(
            payload.AccountPrice ??
              payload.accountPrice ??
              payload.vfp_account_price ??
              payload.VFPAccountPrice ??
              payload.VFPAccountPriceExVat ??
              payload.vafp_rsp_exc_vat ??
              payload.vafp_rsp_inc_vat
          );
          const priceRaw = parseNumeric(
            payload.Price ??
              payload.price ??
              payload.vfp_vafp_rsp_exc_vat ??
              payload.vfp_vafp_rsp_inc_vat ??
              payload.vafp_rsp_exc_vat ??
              payload.vafp_rsp_inc_vat ??
              accountPriceRaw
          );
          const price = Number.isFinite(priceRaw) ? Math.round(priceRaw * 100) / 100 : (Number.isFinite(accountPriceRaw) ? accountPriceRaw : null);

          setSkuDetailsState((prev) => {
            const next = { ...prev, [sku]: { stock, price, loading: false, raw: payload } };
            skuDetailsRef.current = next;
            return next;
          });
          skuPendingRef.current.delete(sku);
        });
      } catch (err) {
        toFetch.forEach((sku) => {
          setSkuDetailsState((prev) => {
            const next = { ...prev, [sku]: { stock: null, price: null, loading: false, error: err?.message || 'Failed to fetch' } };
            skuDetailsRef.current = next;
            return next;
          });
          skuPendingRef.current.delete(sku);
        });
      }
    }

    return normalized.reduce((acc, sku) => {
      acc[sku] = skuDetailsRef.current[sku];
      return acc;
    }, {});
  }, [parseNumeric, obtainOrderwiseToken, orderAccountNumber]);

  const ensureKitAvailability = useCallback(async (kitId) => {
    const kitKey = typeof kitId === 'string' ? kitId.trim() : '';
    if (!kitKey) return null;
    if (kitAvailabilityRef.current[kitKey]) return kitAvailabilityRef.current[kitKey];
    if (kitPendingRef.current.has(kitKey)) return null;
    kitPendingRef.current.add(kitKey);
    try {
      const { data } = await axios.get(`/api/kits/${encodeURIComponent(kitKey)}`);
      const components = Array.isArray(data?.components) ? data.components : [];
      const defaults = components
        .map((component) => {
          const options = Array.isArray(component.SkuOptions) ? component.SkuOptions : [];
          const selected = options.find((opt) => opt.IsDefault) || options.find((opt) => opt.IsAllowed !== false);
          const resolvedSku = (selected?.SKU || selected?.SKUCode || component.SKU || '').trim().toUpperCase();
          const quantity = Number(component.Quantity);
          if (!resolvedSku || !Number.isFinite(quantity) || quantity <= 0) return null;
          return { sku: resolvedSku, quantity };
        })
        .filter(Boolean);

      let builds = defaults.length ? Infinity : 0;
      const detailsMap = defaults.length ? await fetchSkuDetails(defaults.map((entry) => entry.sku)) : {};
      const componentsSummary = defaults.map((entry) => {
        const record = detailsMap[entry.sku] || skuDetailsRef.current[entry.sku];
        const stock = Number.isFinite(record?.stock) ? record.stock : 0;
        const possible = entry.quantity > 0 ? Math.floor(stock / entry.quantity) : 0;
        builds = Math.min(builds, possible);
        return { ...entry, stock, possible };
      });

      if (!Number.isFinite(builds)) builds = 0;

      const summary = { builds, components: componentsSummary };
      setKitAvailabilityState((prev) => {
        const next = { ...prev, [kitKey]: summary };
        kitAvailabilityRef.current = next;
        return next;
      });
      return summary;
    } catch (err) {
      const summary = { builds: null, error: err?.response?.data?.error || err?.message || 'Failed to compute kit availability' };
      setKitAvailabilityState((prev) => {
        const next = { ...prev, [kitKey]: summary };
        kitAvailabilityRef.current = next;
        return next;
      });
      return summary;
    } finally {
      kitPendingRef.current.delete(kitKey);
    }
  }, [fetchSkuDetails]);

  useEffect(() => {
    if (editing.fitmentKey) return;
    if (!showStock) return;
    setKitAvailabilityState(prev => {
      if (!prev || Object.keys(prev).length === 0) return prev;
      const next = {};
      let mutated = false;

      Object.entries(prev).forEach(([kitId, summary]) => {
        if (!summary || !Array.isArray(summary.components) || summary.components.length === 0) {
          next[kitId] = summary;
          return;
        }

        let buildsCalc = summary.components.length ? Infinity : 0;
        const updatedComponents = summary.components.map(component => {
          const record = skuDetailsRef.current[component.sku];
          const stock = Number.isFinite(record?.stock) ? record.stock : 0;
          const possible = component.quantity > 0 ? Math.floor(stock / component.quantity) : 0;
          buildsCalc = Math.min(buildsCalc, possible);
          return {
            ...component,
            stock,
            possible
          };
        });

        if (!Number.isFinite(buildsCalc)) buildsCalc = 0;

        const kitChanged = buildsCalc !== summary.builds || updatedComponents.some((component, idx) => {
          const prevComponent = summary.components[idx];
          return component.stock !== prevComponent?.stock || component.possible !== prevComponent?.possible;
        });

        if (kitChanged) {
          mutated = true;
          next[kitId] = {
            ...summary,
            builds: buildsCalc,
            components: updatedComponents
          };
        } else {
          next[kitId] = summary;
        }
      });

      return mutated ? next : prev;
    });
  }, [skuDetailsState, showStock, editing.fitmentKey]);

  useEffect(() => {
    if (editing.fitmentKey) return;
    if ((!showStock && !(allowTradePrices && showPrices)) || fitments.length === 0) {
      setPricingHydrated(true);
      return;
    }

    let cancelled = false;
    (async () => {
      setPricingHydrated(false);
      try {
        await primeOrderwiseStockCache(false);
      } catch (err) {
        console.warn('OrderWise stock cache prime attempt errored', err);
      }

      if (cancelled) return;

      const skuSet = new Set();
      fitments.forEach(row => {
        const rawSku = row?.SKU;
        if (!rawSku) return;
        rawSku.split(',').forEach(part => {
          const normalized = part.trim().toUpperCase();
          if (normalized) skuSet.add(normalized);
        });
      });
      if (!skuSet.size) return;

      try {
        await fetchSkuDetails(Array.from(skuSet));
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch sku metrics', err);
        }
      } finally {
        if (!cancelled) {
          setPricingHydrated(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fitments, showStock, showPrices, allowTradePrices, fetchSkuDetails, editing.fitmentKey, primeOrderwiseStockCache]);

  useEffect(() => {
    if (editing.fitmentKey) return;
    if (!showStock) return;
    const kitSet = new Set();
    fitments.forEach(row => {
      const kitId = extractKitId(row);
      if (kitId && !kitAvailabilityRef.current[kitId]) {
        kitSet.add(kitId);
      }
    });
    if (!kitSet.size) return;
    let cancelled = false;
    (async () => {
      for (const kitId of kitSet) {
        if (cancelled) break;
        try {
          await ensureKitAvailability(kitId);
        } catch (err) {
          console.error(`Failed to compute kit availability for ${kitId}`, err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fitments, showStock, extractKitId, ensureKitAvailability, editing.fitmentKey]);

  const loadMore = async ({ reset = false } = {}) => {
    if (loading && !reset) return;
    if (!hasMore && !reset) return;

    const offset = reset ? 0 : fitments.length;
    setLoading(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    let aborted = false;
    try {
      // Avoid aborting in-flight requests; always let the latest response win so filters reach the server.
      const clean = {};
      Object.entries(debouncedFilters).forEach(([k, v]) => {
        if (v) clean[k] = v;
      });
      const paramsBag = { ...clean, limit: rowLimit, offset };
      if (debouncedGlobalSearch.trim()) {
        paramsBag.Search = debouncedGlobalSearch.trim();
      }
      const params = new URLSearchParams(paramsBag);
      const res = await axios.get(`/api/fitments?${params}`, { signal: controller.signal });
      const { rows, total } = res.data;
      const totalRecords = total;

      let applied = false;
      setFitments(prev => {
        if (!reset && prev.length !== offset) {
          return prev;
        }
        applied = true;
        return reset ? rows : [...prev, ...rows];
      });

      if (applied) {
        const loadedCount = reset ? rows.length : offset + rows.length;
        const moreAvailable =
          totalRecords == null
            ? rows.length === rowLimit
            : loadedCount < totalRecords;
        setTotalRows(totalRecords ?? loadedCount);
        setHasMore(rows.length > 0 && moreAvailable);
      }
    } catch (e) {
      if (axios.isCancel(e) || e?.name === 'AbortError' || e?.code === 'ERR_CANCELED') {
        aborted = true;
        return;
      }
      console.error('loadMore error', e);
    } finally {
      if (!aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setHasMore(true);
    setTotalRows(0);
    setFitments([]);
    loadMore({ reset: true });
    ['ModelName', 'OEMNumber'].forEach(key => {
      const term = debouncedFilters[key]?.trim();
      if (term) {
        setRecentSearches(prev => {
          const entry = { key, value: term };
          const deduped = prev.filter(i => !(i.key === key && i.value === term));
          const updated = [entry, ...deduped].slice(0, 10);
          localStorage.setItem('recentSearches', JSON.stringify(updated));
          return updated;
        });
      }
    });
    if (debouncedGlobalSearch.trim()) {
      setRecentSearches(prev => {
        const entry = { key: 'Search', value: debouncedGlobalSearch.trim() };
        const deduped = prev.filter(i => !(i.key === entry.key && i.value === entry.value));
        const updated = [entry, ...deduped].slice(0, 10);
        localStorage.setItem('recentSearches', JSON.stringify(updated));
        return updated;
      });
    }
  }, [debouncedFilters, debouncedGlobalSearch, rowLimit]);

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !loading && hasMore && !isFetching.current) {
        isFetching.current = true;
        loadMore().finally(() => { isFetching.current = false; });
      }
    }, { root: document.getElementById('grid'), threshold: 0 });
    if (lastRowRef.current) {
      observer.observe(lastRowRef.current);
    }
    return () => {
      if (lastRowRef.current) {
        observer.unobserve(lastRowRef.current);
      }
    };
  }, [loading, hasMore, lastRowRef, fitments]);

  useEffect(() => {
    const gridDiv = document.getElementById('grid');
    if (!gridDiv) return;
    const handleScroll = () => {
      const rowHeight = 45;
      const scrollTop = gridDiv.scrollTop;
      const visibleStart = Math.floor(scrollTop / rowHeight);
      setCurrentViewPage(Math.floor(visibleStart / rowLimit) + 1);
    };
    gridDiv.addEventListener('scroll', handleScroll);
    return () => gridDiv.removeEventListener('scroll', handleScroll);
  }, [fitments.length, rowLimit]);

  useEffect(() => {
    const gridEl = gridRef.current;
    const filtersEl = filtersScrollRef.current;
    if (!gridEl || !filtersEl) return undefined;

    const syncFilters = () => {
      filtersEl.scrollLeft = gridEl.scrollLeft;
    };
    const syncGrid = () => {
      gridEl.scrollLeft = filtersEl.scrollLeft;
    };

    gridEl.addEventListener('scroll', syncFilters);
    filtersEl.addEventListener('scroll', syncGrid);

    return () => {
      gridEl.removeEventListener('scroll', syncFilters);
      filtersEl.removeEventListener('scroll', syncGrid);
    };
  }, [fitments.length]);

  const startEdit = (fitmentKey, field, current) =>
    setEditing({ fitmentKey, field, value: current || '' });

  const commitEdit = async () => {
    const { fitmentKey, field, value } = editing;
    try {
      const payloadValue = (typeof value === 'string' && value.trim() === '') ? null : value;
      const res = await axios.patch(
        `/api/fitments/${fitmentKey}`,
        { field, value: payloadValue }
      );
      const updated = res.data.updated;
      setFitments(fitments.map(r => {
        if (field === 'OEMNumber') return r.FitmentKey === fitmentKey ? { ...r, ...updated } : r;
        if (['OEMNote', 'SKUGroup'].includes(field)) return r.OEMNumber === updated.OEMNumber ? { ...r, ...updated } : r;
        return r.FitmentKey === fitmentKey ? { ...r, ...updated } : r;
      }));
      loadMore();
      setFitments(curr =>
        curr.map(r => {
          const isOwnRow = r.FitmentKey === fitmentKey;
          const isGroupKey =
            ['OEMNote', 'SKUGroup'].includes(field)
            && r.OEMType !== 'Misc';
          const isGroupRow = isGroupKey && r.OEMNumber === updated.OEMNumber;
          if (!isOwnRow && !isGroupRow) return r;
          const rf = new Set(
            (r.RecentFields || '').split(',').map(x => x.trim()).filter(Boolean)
          );
          rf.add(field);
          return { ...r, RecentFields: Array.from(rf).join(',') };
        })
      );
    } catch (err) {
      console.error('Update failed', err);
      alert('Save failed—see console.');
    } finally {
      setEditing({ fitmentKey: null, field: '', value: '' });
    }
  };

  const handleRevert = async entry => {
    if (!canEditFitments) {
      window.alert('You do not have permission to revert history entries.');
      return;
    }
    const ok = window.confirm(
      `Revert "${entry.FieldName}" on ${entry.Make} ${entry.ModelName} ${entry.Year} (${entry.ItemType})?\n` +
      `from "${entry.NewValue}" back to "${entry.OldValue}"?`
    );
    if (!ok) return;
    await axios.patch(
      `/api/fitments/${entry.FitmentKey}`,
      { field: entry.FieldName, value: entry.OldValue }
    );
    loadMore();
    openHistory(entry.FitmentKey);
  };

  const handleRedo = async entry => {
    if (!canEditFitments) {
      window.alert('You do not have permission to re-apply history entries.');
      return;
    }
    const ok = window.confirm(
      `Re-apply "${entry.FieldName}" on ${entry.Make} ${entry.ModelName} ${entry.Year} (${entry.ItemType})?\n` +
      `to "${entry.NewValue}"?`
    );
    if (!ok) return;
    await axios.patch(
      `/api/fitments/${entry.FitmentKey}`,
      { field: entry.FieldName, value: entry.NewValue }
    );
    loadMore();
    openHistory(entry.FitmentKey);
  };

  const handleKey = e => {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') setEditing({ fitmentKey: null, field: '', value: '' });
  };

  const handleFilterChange = (e, key) => {
    const val = e.target.value;
    setFilters(f => ({ ...f, [key]: val }));
    repullOrderwiseStockCacheIfExpired();
  };

  const totalPages = Math.ceil(totalRows / rowLimit);

  const openHistory = async fitmentKey => {
    setHistoryFitmentKey(fitmentKey);
    try {
      const res = await axios.get(
        `/api/fitments/history?fitmentKey=${fitmentKey}`
      );
      setHistoryRecords(res.data);
      setHistoryVisible(true);
    } catch (err) {
      console.error('Failed to fetch history', err);
    }
  };

  const closeHistory = () => setHistoryVisible(false);

  const refreshFitmentRow = useCallback(async (fitmentKey) => {
    if (!fitmentKey) return null;
    try {
      const { data } = await axios.get(`/api/fitments/${fitmentKey}`);
      if (data) {
        setFitments(prev => prev.map(row => (row.FitmentKey === fitmentKey ? { ...row, ...data } : row)));
      }
      return data;
    } catch (err) {
      console.error('Failed to refresh fitment row', err);
      return null;
    }
  }, []);

  const openKitModalForRow = useCallback(async (row, rowIndex = null) => {
    if (!canOpenKitModal) {
      window.alert('You do not have permission to view kits.');
      return;
    }
    if (!row) return;
    try {
      let latest = row;
      let fitmentKey = row?.FitmentKey ?? null;
      if (fitmentKey) {
        const refreshed = await refreshFitmentRow(fitmentKey);
        if (refreshed) {
          latest = { ...row, ...refreshed };
          fitmentKey = refreshed.FitmentKey ?? fitmentKey;
        }
      }
      const resolvedIndex = rowIndex ?? (latest?.FitmentKey ? fitments.findIndex(r => r.FitmentKey === latest.FitmentKey) : fitments.indexOf(row));
      const fitmentContext = {
        FitmentKey: fitmentKey ?? null,
        ItemTypeKey: latest?.ItemTypeKey ?? null,
        ItemType: latest?.ItemType ?? '',
        FitmentLocationKey: latest?.FitmentLocationKey ?? null,
        FitmentLocation: latest?.FitmentLocation ?? '',
        OEMNumber: latest?.OEMNumber ?? '',
        SKUGroup: latest?.SKUGroup ?? '',
        ModelKey: latest?.ModelKey ?? null,
        Make: latest?.Make ?? '',
        ModelName: latest?.ModelName ?? '',
        Year: latest?.Year ?? null,
        Region: latest?.Region ?? null,
        Quantity: latest?.Quantity ?? null,
        OEMNote: latest?.OEMNote ?? '',
        rowIndex: resolvedIndex >= 0 ? resolvedIndex : null,
        baseRow: latest
      };
      setKitModalState({
        open: true,
        kitId: extractKitId(latest),
        fitment: fitmentContext
      });
    } catch (err) {
      console.error('Failed to open kit modal from PartFinder', err);
      window.alert('Failed to open the kit modal. See console for details.');
    }
  }, [canOpenKitModal, fitments, refreshFitmentRow, extractKitId]);

  const handleKitModalClose = useCallback(() => {
    const keyToRefresh = kitModalState.fitment?.FitmentKey ?? null;
    setKitModalState({ open: false, kitId: null, fitment: null });
    if (keyToRefresh) {
      refreshFitmentRow(keyToRefresh).catch(err => {
        console.error('Failed to refresh fitment after closing kit modal', err);
      });
    }
  }, [kitModalState.fitment, refreshFitmentRow]);

  const handleKitLinked = useCallback(async (kitId, meta = {}) => {
    if (!kitId) return;
    const fitmentKey = meta.fitmentKey ?? kitModalState.fitment?.FitmentKey ?? null;
    if (!fitmentKey) return;
    try {
      await refreshFitmentRow(fitmentKey);
    } catch (err) {
      console.error('Failed to refresh fitment after linking kit', err);
    }
    setKitModalState(prev => {
      if (!prev.open) return prev;
      return {
        open: true,
        kitId,
        fitment: prev.fitment ? { ...prev.fitment, FitmentKey: fitmentKey } : prev.fitment
      };
    });
  }, [kitModalState.fitment, refreshFitmentRow]);

  const renderSkuCell = useCallback((row) => {
    const rawSku = row?.SKU;
    const kitId = extractKitId(row);
    const showPrice = allowTradePrices && showPrices;
    const showStockInfo = showStock;

    const parts = typeof rawSku === 'string'
      ? rawSku.split(',').map(part => part.trim()).filter(Boolean)
      : [];

    if (!parts.length) {
      const trimmed = typeof rawSku === 'string' ? rawSku.trim() : '';
      if (trimmed) return rawSku;
      if (kitId && showStockInfo) {
        const summary = kitAvailabilityState[kitId];
        if (summary) {
          if (summary.builds == null) return 'Buildable ?';
          return `Buildable ${summary.builds}`;
        }
        if (kitPendingRef.current.has(kitId)) return 'Buildable ...';
        return 'Buildable ?';
      }
      return '–';
    }

    return parts.map((sku, index) => {
      const upper = sku.toUpperCase();
      const detail = skuDetailsState[upper];
      const tokens = [sku];

      if (showStockInfo) {
        if (detail?.loading) tokens.push('...');
        else if (detail && Number.isFinite(detail.stock)) tokens.push(String(detail.stock));
        else if (detail?.error) tokens.push('!');
        else tokens.push('?');
      }

      if (kitId && showStockInfo && index === 0) {
        const summary = kitAvailabilityState[kitId];
        if (summary) {
          if (summary.builds == null) tokens.push('Buildable ?');
          else tokens.push(`Buildable ${summary.builds}`);
        } else if (kitPendingRef.current.has(kitId)) {
          tokens.push('Buildable ...');
        } else {
          tokens.push('Buildable ?');
        }
      }

      if (showPrice) {
        if (detail?.loading) tokens.push('...');
        else if (detail && Number.isFinite(detail.price)) {
          tokens.push(detail.price.toFixed(2));
        } else if (detail?.error) {
          tokens.push('!');
        } else {
          tokens.push('?');
        }
      }

      return tokens.join(' - ');
    }).join(', ');
  }, [allowTradePrices, showPrices, showStock, extractKitId, skuDetailsState, kitAvailabilityState]);

  const handleRecentSelect = sel => {
    if (sel?.key === 'Search') {
      setGlobalSearch(sel.value || '');
      setPage(1);
      repullOrderwiseStockCacheIfExpired();
      return;
    }
    setFilters(f => ({ ...f, [sel.key]: sel.value }));
    setPage(1);
    repullOrderwiseStockCacheIfExpired();
  };

  const addDraftFor = useCallback((fitmentKey, row) => {
    const existing = addDrafts[fitmentKey];
    if (existing) return existing;
    const skus = typeof row?.SKU === 'string'
      ? row.SKU.split(',').map(s => s.trim()).filter(Boolean)
      : [];
    const primarySku = skus[0] || '';
    const defaultNote = [row?.Make, row?.ModelName, row?.Year, row?.ItemType, row?.FitmentLocation, row?.OEMNumber]
      .filter(Boolean)
      .join(' · ');
    return { quantity: 1, sku: primarySku, note: defaultNote };
  }, [addDrafts]);

  const updateAddDraft = useCallback((fitmentKey, updater) => {
    setAddDrafts(prev => {
      const next = { ...prev };
      const current = prev[fitmentKey];
      next[fitmentKey] = typeof updater === 'function' ? updater(current) : updater;
      return next;
    });
  }, []);

  const handleAddToOrder = useCallback((row) => {
    if (!onAddToOrder) return;
    const draft = addDraftFor(row.FitmentKey, row);
    const quantity = Number(draft?.quantity) || 1;
    const sku = draft?.sku || '';
    const note = (draft?.note || '').trim();
    onAddToOrder({
      fitment: row,
      sku,
      quantity,
      note,
    });
    if (addFeedbackTimeoutRef.current) clearTimeout(addFeedbackTimeoutRef.current);
    setAddFeedback(`Added ${sku || 'item'} (qty ${quantity})`);
    addFeedbackTimeoutRef.current = setTimeout(() => setAddFeedback(''), 2000);
  }, [onAddToOrder, addDraftFor]);

  const addControlRefs = useRef(new Map());

  const probeImage = useCallback(async (url) => {
    if (!url) return false;
    if (imageProbeCacheRef.current.has(url)) return imageProbeCacheRef.current.get(url);
    const result = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
    imageProbeCacheRef.current.set(url, result);
    return result;
  }, []);

  const openImageModalForRow = useCallback(async (row) => {
    if (!row) return;
    const skus = typeof row?.SKU === 'string'
      ? row.SKU.split(',').map((s) => s.trim()).filter(Boolean)
      : [];
    const filtered = skus.filter((s) => s.toUpperCase() !== '999999H');
    if (!filtered.length) {
      setAddFeedback('No images available');
      return;
    }
    const candidates = filtered.flatMap((sku) => [1, 2, 3].map((n) => ({ sku, url: `https://hendler.co.uk/images/products/${sku}_${n}.jpg` })));
    const checks = await Promise.all(candidates.map(async (c) => (await probeImage(c.url)) ? c : null));
    const items = checks.filter(Boolean);
    if (!items.length) {
      setAddFeedback('No images available');
      return;
    }
    setImageModal({ open: true, items, index: 0, label: items[0].sku || '' });
  }, [probeImage]);

  const focusRow = useCallback((rowIdx, { focusInputs = true } = {}) => {
    if (!fitments.length) return;
    const clamped = Math.min(Math.max(rowIdx, 0), fitments.length - 1);
    setActiveRowIndex(clamped);
    const fitmentKey = fitments[clamped]?.FitmentKey ?? fitments[clamped]?.fitmentKey ?? clamped;
    const refs = addControlRefs.current.get(fitmentKey);
    const target = refs?.sku || refs?.qty || refs?.add;
    if (focusInputs && target) {
      target.focus();
      target.select?.();
    }
    if (focusInputs) {
      setPendingSkuFocus(false);
    }
  }, [fitments]);

  useEffect(() => {
    if (!fitments.length) {
      setPendingSkuFocus(false);
    }
  }, [fitments.length]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const isForm = ['input', 'textarea', 'select', 'option', 'button'].includes(tag);

      if (imageModal.open) {
        if (e.key === 'Escape') {
          e.preventDefault();
          setImageModal({ open: false, items: [], index: 0, label: '' });
          return;
        }
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          setImageModal((prev) => {
            const total = prev.items.length;
            if (!total) return prev;
            const delta = e.key === 'ArrowRight' ? 1 : -1;
            const next = (prev.index + delta + total) % total;
            return { ...prev, index: next, label: prev.items[next]?.sku || prev.label };
          });
          return;
        }
      }

      if (e.key === 'F3') {
        e.preventDefault();
        focusRow(0, { focusInputs: false });
        setPendingSkuFocus(false);
        gridRef.current?.focus?.();
        return;
      }

      if (e.key === 'F2') {
        e.preventDefault();
        modelNameInputRef.current?.focus?.();
        modelNameInputRef.current?.select?.();
        return;
      }

      if (e.key === 'F4' && activeRowIndex >= 0) {
        e.preventDefault();
        openImageModalForRow(fitments[activeRowIndex]);
        return;
      }

      if (isForm) return;

      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && activeRowIndex >= 0) {
        e.preventDefault();
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        focusRow(activeRowIndex + delta, { focusInputs: false });
        setPendingSkuFocus(false);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeRowIndex, focusRow, fitments, openImageModalForRow, imageModal.open]);

  const renderAddControls = (row, rowIdx) => {
    if (!onAddToOrder) return null;
    const draft = addDraftFor(row.FitmentKey, row);
    const skus = typeof row?.SKU === 'string'
      ? row.SKU.split(',').map(s => s.trim()).filter(Boolean)
      : [];

    const ensureRef = (fitmentKey, key, el) => {
      const entry = addControlRefs.current.get(fitmentKey) || {};
      entry[key] = el;
      addControlRefs.current.set(fitmentKey, entry);
    };

    const renderSkuOption = (sku) => {
      const upper = (sku || '').toUpperCase();
      const detail = skuDetailsState[upper];
      const stock = Number(detail?.stock);
      const outOfStock = showStock && detail && Number.isFinite(stock) && stock <= 0;
      return (
        <option
          key={sku || 'blank'}
          value={sku}
          style={outOfStock ? { backgroundColor: '#ffe5e5' } : undefined}
        >
          {sku}
        </option>
      );
    };

    return (
      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: '0.35rem', alignItems: 'center' }}
        data-add-row-idx={rowIdx}
      >
        <select
          value={draft.sku || skus[0] || ''}
          onChange={(e) => updateAddDraft(row.FitmentKey, { ...draft, sku: e.target.value })}
          ref={(el) => ensureRef(row.FitmentKey, 'sku', el)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddToOrder(row);
            }
          }}
        >
          {skus.length ? skus.map(renderSkuOption) : renderSkuOption('')}
        </select>
        <input
          type="number"
          min={1}
          value={draft.quantity || 1}
          onChange={(e) => updateAddDraft(row.FitmentKey, { ...draft, quantity: Number(e.target.value) })}
          style={{ width: '100%' }}
          aria-label="Quantity"
          ref={(el) => ensureRef(row.FitmentKey, 'qty', el)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddToOrder(row);
            }
          }}
        />
        <button
          type="button"
          onClick={() => handleAddToOrder(row)}
          style={{ gridColumn: 'span 2' }}
          ref={(el) => ensureRef(row.FitmentKey, 'add', el)}
        >
          Add to order
        </button>
      </div>
    );
  };

  const getColumnWidth = (key) => {
    switch (key) {
      case 'Make': return '10%';
      case 'ModelName': return '20%';
      case 'ModelID': return '8%';
      case 'Year': return '5%';
      case 'Region': return '8%';
      case 'ItemType': return '10%';
      case 'FitmentLocation': return '12%';
      case 'OEMNumber': return '10%';
      case 'SKU': return '10%';
      case 'Quantity': return '5%';
      case 'OEMNote': return '10%';
      case 'AdditionalFitmentInfo': return '15%';
      case 'Images': return '5%';
      case 'History': return '5%';
      default: return 'auto';
    }
  };

  if (permissions?.PartFinder === 'none') {
    return <div>Unauthorized access to PartFinder.</div>;
  }

  return (
    <section className="pf-shell" style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="pf-shell__header">
        <h2 className="pf-shell__header-title">PartFinder</h2>
        <button
          type="button"
          className="pim-chip"
          onClick={() => setShowVehicleLookup(true)}
          title="Look up a UK registration or VIN"
        >
          🚗 VRN / VIN Lookup
        </button>
        <button
          type="button"
          className="pim-chip"
          onClick={() => setShowHistoryHighlights(v => !v)}
          aria-pressed={showHistoryHighlights}
        >
          {showHistoryHighlights ? 'Highlights on' : 'Highlights off'} · Rows: {totalRows}
        </button>
      </div>

      <div className="pf-controls">
        <div className="pf-controls__row">
          <div className="pf-controls__search">
            <label htmlFor="pf-global-search">Search:</label>
            <input
              id="pf-global-search"
              value={globalSearch}
              onChange={(e) => {
                setGlobalSearch(e.target.value);
                repullOrderwiseStockCacheIfExpired();
              }}
              ref={modelNameInputRef}
              placeholder="All columns (e.g. cbr 2017-2020)"
              onKeyDown={e => {
                if (!isOrderEntryEmbed) return;
                if (e.key === 'Tab' && !e.shiftKey) {
                  if (fitments.length) {
                    e.preventDefault();
                    focusRow(0, { focusInputs: false });
                    setPendingSkuFocus(true);
                    gridRef.current?.focus?.();
                  }
                }
              }}
            />
          </div>
          <button
            className="reset-filters-button"
            onClick={() => {
              setFilters(defaultFilters);
              setGlobalSearch('');
              setPage(1);
              setRecentSelectValue('');
              repullOrderwiseStockCacheIfExpired();
            }}
          >
            Clear All Filters
          </button>
          <div className="pf-controls__recent">
            <label htmlFor="pf-recent-select">Recent:</label>
            <select
              id="pf-recent-select"
              value={recentSelectValue}
              onChange={e => {
                const sel = JSON.parse(e.target.value);
                handleRecentSelect(sel);
                setRecentSelectValue(e.target.value);
              }}
            >
              <option value="" disabled>— pick one —</option>
              {recentSearches.map((item, i) => (
                <option key={i} value={JSON.stringify(item)}>
                  {item.key === 'ModelName' ? 'Model: ' : item.key === 'Search' ? 'Search: ' : 'OEM: '}{item.value}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="pf-controls__toggles">
          <label>
            <input
              type="checkbox"
              checked={showStock}
              onChange={e => {
                setShowStock(e.target.checked);
                repullOrderwiseStockCacheIfExpired();
              }}
            />
            Show stock / buildable qty
          </label>
          {allowTradePrices && (
            <label>
              <input
                type="checkbox"
                checked={showPrices}
                onChange={e => {
                  setShowPrices(e.target.checked);
                  repullOrderwiseStockCacheIfExpired();
                }}
              />
              Show trade prices
            </label>
          )}
        </div>
      </div>

      {addFeedback && (
        <div style={{ margin: '0.35rem 0', color: '#2563eb', fontWeight: 600 }}>
          {addFeedback}
        </div>
      )}

      <div className="pf-filters" ref={filtersScrollRef} style={{ overflowX: 'auto' }}>
        <div ref={tableRef} className="pf-filters__row">
          {Object.keys(defaultFilters).map(key => (
            <div key={key} className="pf-filters__field">
              <label htmlFor={`filter-${key}`}>{key}</label>
              <input
                id={`filter-${key}`}
                value={filters[key]}
                onChange={e => handleFilterChange(e, key)}
              />
            </div>
          ))}
          <div className="pf-filters__field pf-filters__field--accent" style={colStyle(ACTION_COL)}>Images</div>
          {!hideManagementColumns && (
            <div className="pf-filters__field pf-filters__field--accent" style={colStyle(ACTION_COL)}>History</div>
          )}
          {!hideManagementColumns && (
            <div className="pf-filters__field pf-filters__field--accent" style={colStyle(ACTION_COL)}>OEM</div>
          )}
          {!hideManagementColumns && (
            <div className="pf-filters__field pf-filters__field--accent" style={colStyle(ACTION_COL)}>SKU</div>
          )}
          {onAddToOrder && (
            <div className="pf-filters__field pf-filters__field--accent" style={colStyle(ADD_COLUMN_WIDTH)}>Add</div>
          )}
        </div>
      </div>
      <div className="pf-grid-shell" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div
          id="grid"
          ref={gridRef}
          className={`${isDark ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'} pf-grid`}
          style={{ flex: 1, minHeight: 0, overflow: 'auto' }}
          tabIndex={-1}
          onKeyDown={(e) => {
            if (!isOrderEntryEmbed) return;
            const tag = (e.target?.tagName || '').toLowerCase();
            const isInteractive = ['input', 'select', 'textarea', 'button'].includes(tag);

            if (e.key === 'Tab' && !e.shiftKey && !isInteractive) {
              e.preventDefault();
              setPendingSkuFocus(false);
              if (!fitments.length) return;
              const targetIdx = activeRowIndex >= 0 ? activeRowIndex : 0;
              focusRow(targetIdx, { focusInputs: true });
            }
          }}
        >
        {fitments.flatMap((row, index) => {
          const prevRow = index > 0 ? fitments[index - 1] : null;
          const showDivider = prevRow && row.ModelKey !== prevRow.ModelKey;
          const days30Ago = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
          const createdDateRaw = row.DateCreated ? new Date(row.DateCreated) : null;
          const createdDate = createdDateRaw && !Number.isNaN(createdDateRaw.getTime()) ? createdDateRaw : null;
          const highlightColor = isDark ? '#00821E' : '#90EE90';
          const rowBgColor = (showHistoryHighlights && createdDate && createdDate > days30Ago) ? highlightColor : undefined;
          const hasRecent = showHistoryHighlights && row.HasRecentChanges === 1;
          const kitId = extractKitId(row);
          const isActive = activeRowIndex === index;
          const activeBgColor = isActive ? (isDark ? '#243b70' : '#e0edff') : undefined;
          const skus = typeof row?.SKU === 'string'
            ? row.SKU.split(',').map((s) => s.trim()).filter(Boolean)
            : [];
          const selectedSku = (addDrafts[row.FitmentKey]?.sku || skus[0] || '').toUpperCase();
          const selectedSkuDetail = selectedSku ? skuDetailsState[selectedSku] : null;
          const selectedStock = Number(selectedSkuDetail?.stock);
          const isOutOfStockLine = highlightOutOfStockRows && showStock && selectedSkuDetail && Number.isFinite(selectedStock) && selectedStock <= 0;
          const baseBgColor = activeBgColor || (isOutOfStockLine ? '#ffe5e5' : rowBgColor);
          const rowDiv = (
            <div
              key={row.FitmentKey || index}
              ref={(el) => {
                const key = row.FitmentKey || row.fitmentKey || index;
                if (el) rowRefs.current.set(key, el);
                else rowRefs.current.delete(key);
              }}
              className="pf-grid__row"
              style={{
                backgroundColor: baseBgColor,
                outline: isActive ? '2px solid #2563eb' : undefined,
                boxShadow: isActive ? '0 0 0 2px rgba(37,99,235,0.35)' : undefined
              }}
              tabIndex={0}
              onClick={() => focusRow(index)}
            >
              {Object.keys(defaultFilters).map((field, colIdx) => {
                const isEditing = editing.fitmentKey === row.FitmentKey && editing.field === field;
                const display = row[field];
                const cellBgColor = (!rowBgColor && hasRecent) ? 'orange' : undefined;
                const thWidth = tableRef.current?.children[colIdx]?.offsetWidth || 120;
                const isKitField = field === 'OEMNumber';
                let content;

                if (isEditing) {
                  content = (
                    <input
                      autoFocus
                      value={editing.value}
                      onChange={e => setEditing(ed => ({ ...ed, value: e.target.value }))}
                      onBlur={commitEdit}
                      onKeyDown={handleKey}
                      style={{ width: '100%', boxSizing: 'border-box' }}
                    />
                  );
                } else if (isKitField && canOpenKitModal && kitId) {
                  const rawLabel = display != null ? String(display).trim() : '';
                  const linkLabel = rawLabel || kitId;
                  content = (
                    <button
                      type="button"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        openKitModalForRow(row, index);
                      }}
                      onDoubleClick={e => e.stopPropagation()}
                      style={{
                        padding: 0,
                        margin: 0,
                        background: 'none',
                        border: 'none',
                        color: '#2563eb',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        width: '100%',
                        textAlign: 'left'
                      }}
                    >
                      {linkLabel}
                    </button>
                  );
                } else {
                  content = field === 'SKU'
                    ? renderSkuCell(row)
                    : (display == null ? '–' : display);
                }

                return (
                  <div
                    key={field}
                    className="pf-grid__cell"
                    style={{
                      width: `${thWidth}px`,
                      backgroundColor: cellBgColor,
                      cursor: editableFields.includes(field) && canEditFitments ? 'pointer' : 'default'
                    }}
                    onDoubleClick={() => {
                      if (editableFields.includes(field) && canEditFitments) {
                        startEdit(row.FitmentKey, field, display);
                      }
                    }}
                  >
                    {content}
                  </div>
                );
              })}
              <div className="pf-grid__cell pf-grid__cell--action" style={colStyle(ACTION_COL)}>
                <PartImages params={{ data: row }} pricingReady={pricingHydrated} />
              </div>
              {!hideManagementColumns && (
                <div className="pf-grid__cell pf-grid__cell--action" style={colStyle(ACTION_COL)}>
                  <button onClick={() => openHistory(row.FitmentKey)}>History</button>
                </div>
              )}
              {!hideManagementColumns && (
                <div className="pf-grid__cell pf-grid__cell--action" style={colStyle(ACTION_COL)}>
                  <button onClick={() => {
                    setSelectedOEMNumber(row.OEMNumber || '');
                    setSelectedOEMKey(row.OEMKey || null);
                    setOemModalVisible(true);
                  }}>OEM</button>
                </div>
              )}
              {!hideManagementColumns && (
                <div className="pf-grid__cell pf-grid__cell--action" style={colStyle(ACTION_COL)}>
                  <button onClick={() => {
                    setSelectedSKUGroup(row.SKUGroup || '');
                    setSkuModalVisible(true);
                  }}>SKU</button>
                </div>
              )}
              {onAddToOrder && (
                <div className="pf-grid__cell pf-grid__cell--action" style={colStyle(ADD_COLUMN_WIDTH)}>
                  {renderAddControls(row, index)}
                </div>
              )}
            </div>
          );
          return showDivider ? [
            <div key={`divider-${index}`} className="pf-divider" />,
            rowDiv
          ] : [rowDiv];
        })}

          {hasMore && !loading && <div ref={lastRowRef} style={{ height: '1px' }} />}

          {loading && (
            <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
              Loading more rows…
            </div>
          )}

          {!hasMore && !loading && (
            <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
              All {totalRows} rows loaded
            </div>
          )}
        </div>
        <div className="pf-meta">
          Page {currentViewPage} / {totalPages} (Total Rows: {totalRows})
        </div>
      </div>

        {imageModal.open && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 3000,
            }}
            role="dialog"
            aria-modal="true"
          >
            <div style={{ background: '#fff', padding: '1rem', borderRadius: '8px', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                <div style={{ fontWeight: 600 }}>Images for {imageModal.label || 'selection'}</div>
                <button type="button" onClick={() => setImageModal({ open: false, items: [], index: 0, label: '' })}>Close</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={() => setImageModal((prev) => {
                    const total = prev.items.length;
                    if (!total) return prev;
                    const next = (prev.index - 1 + total) % total;
                    return { ...prev, index: next, label: prev.items[next]?.sku || prev.label };
                  })}
                >
                  ⟨ Prev
                </button>
                <img
                  src={imageModal.items[imageModal.index]?.url}
                  alt={imageModal.items[imageModal.index]?.sku || 'Image'}
                  style={{ maxWidth: '70vw', maxHeight: '70vh', objectFit: 'contain', border: '1px solid #ccc', borderRadius: '6px' }}
                />
                <button
                  type="button"
                  onClick={() => setImageModal((prev) => {
                    const total = prev.items.length;
                    if (!total) return prev;
                    const next = (prev.index + 1) % total;
                    return { ...prev, index: next, label: prev.items[next]?.sku || prev.label };
                  })}
                >
                  Next ⟩
                </button>
              </div>
              <div style={{ textAlign: 'center', color: '#555' }}>
                {imageModal.index + 1} / {imageModal.items.length}
              </div>
            </div>
          </div>
        )}

      {historyVisible && (
        <div className="history-modal">
          <button onClick={() => setHistoryVisible(false)}>Close</button>
          <h3>History for Fitment {historyFitmentKey}</h3>
          <table border="1" cellPadding="4" cellSpacing="0" width="100%">
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>ModelKey</th>
                <th>Make</th>
                <th>Model</th>
                <th>Year</th>
                <th>ItemType</th>
                <th>FitmentLocation</th>
                <th>Field</th>
                <th>Old</th>
                <th>New</th>
                <th>Undo</th>
                <th>Redo</th>
              </tr>
            </thead>
            <tbody>
              {historyRecords.map((h, i) => (
                <tr key={i}>
                  <td>{new Date(h.ChangeTime).toLocaleString()}</td>
                  <td>{h.ChangedBy}</td>
                  <td>{h.ModelKey ?? '—'}</td>
                  <td>{h.Make}</td>
                  <td>{h.ModelName}</td>
                  <td>{h.Year}</td>
                  <td>{h.ItemType}</td>
                  <td>{h.FitmentLocation}</td>
                  <td>{h.FieldName}</td>
                  <td>{h.OldValue}</td>
                  <td>{h.NewValue}</td>
                  <td>
                    <button
                      onClick={() => handleRevert(h)}
                      disabled={!canEditFitments}
                      title={canEditFitments ? 'Undo change' : 'Requires write permission'}
                    >
                      Undo
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() => handleRedo(h)}
                      disabled={!canEditFitments}
                      title={canEditFitments ? 'Redo change' : 'Requires write permission'}
                    >
                      Redo
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {oemModalVisible && (
        <ManageOEMModal
          open={oemModalVisible}
          onClose={() => setOemModalVisible(false)}
          initialOEMNumber={selectedOEMNumber}
          initialOEMKey={selectedOEMKey}
          canEdit={canEditFitments}
        />
      )}
      {skuModalVisible && (
        <ManageSKUModal
          open={skuModalVisible}
          onClose={() => setSkuModalVisible(false)}
          initialSKUGroup={selectedSKUGroup}
          isDark={isDark}
          canEdit={canEditFitments}
        />
      )}
      <KittingModal
        open={kitModalState.open}
        onClose={handleKitModalClose}
        initialKitId={kitModalState.kitId}
        fitmentContext={kitModalState.fitment}
        canEdit={canEditKits}
        isDark={isDark}
        onKitLinked={handleKitLinked}
      />
      {showVehicleLookup && (
        <VehicleLookupModal
          isDark={isDark}
          onClose={() => setShowVehicleLookup(false)}
          onSelectVehicle={(v) => {
            setShowVehicleLookup(false);
            // Prefill global search with make + model (+ year if known) to filter fitments
            const parts = [v?.make, v?.model].filter(Boolean);
            const year = v?.yearOfManufacture || (v?.manufactureDate ? new Date(v.manufactureDate).getFullYear() : null);
            if (year) parts.push(String(year));
            const term = parts.join(' ').trim();
            if (term) {
              setGlobalSearch(term);
              setPage(1);
            }
          }}
        />
      )}
    </section>
  );
}
